import React, { useState, useEffect, useCallback, memo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    CircularProgress,
    Select,
    MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { inputStyles, getInputStyles } from './styles';
import { validateProjects } from './validation';
import { MONTHS, YEAR_RANGE, parseDateString } from './constants';

const ProjectsSection = memo(({ 
    initialData, 
    authAxios, 
    setError, 
    onUpdate 
}) => {
    const [projects, setProjects] = useState(initialData || []);
    const [saving, setSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(true);

    useEffect(() => {
        if (initialData) {
            // Parse dates from API (YYYY-MM-DD) to month/year components
            const parsedData = initialData.map(proj => {
                const startParsed = parseDateString(proj.start_date);
                const endParsed = parseDateString(proj.end_date);
                return {
                    ...proj,
                    start_month: startParsed.month,
                    start_year: startParsed.year,
                    end_month: endParsed.month,
                    end_year: endParsed.year,
                };
            });
            setProjects(parsedData);
        }
    }, [initialData]);

    const addProject = useCallback(() => {
        setProjects(prev => [...prev, {
            id: Date.now(),
            name: '',
            description: '',
            start_month: '',
            start_year: '',
            end_month: '',
            end_year: '',
        }]);
    }, []);

    const updateProject = useCallback((id, field, value) => {
        setProjects(prev => prev.map(proj => 
            proj.id === id ? { ...proj, [field]: value } : proj
        ));
    }, []);

    const removeProject = useCallback((id) => {
        setProjects(prev => prev.filter(proj => proj.id !== id));
    }, []);

    // Format date for API (YYYY-MM-DD)
    const formatDate = (month, year) => {
        if (!month || !year) return null;
        return `${year}-${month.padStart(2, '0')}-01`;
    };

    const saveProjectEntry = async (proj) => {
        try {
            const payload = {
                name: proj.name,
                description: proj.description || '',
                start_date: formatDate(proj.start_month, proj.start_year),
                end_date: formatDate(proj.end_month, proj.end_year),
            };

            if (proj.id && typeof proj.id === 'number' && proj.id > 1000000000000) {
                const response = await authAxios.post('/projects/', payload);
                return response.data;
            } else {
                const response = await authAxios.patch(`/projects/${proj.id}/`, payload);
                return response.data;
            }
        } catch (error) {
            console.error('Failed to save project:', error);
            throw error;
        }
    };

    const handleSaveAll = async () => {
        const errors = validateProjects(projects);
        if (errors.length > 0) {
            setShowErrors(true);
            const fieldErrors = errors.map(e => {
                const fields = Object.keys(e).filter(k => k !== 'index');
                return `Entry ${e.index + 1}: ${fields.join(', ')}`;
            });
            setError?.(`Projects validation failed: ${fieldErrors.join('; ')}`);
            return;
        }


        setSaving(true);

        try {
            const savedEntries = await Promise.all(
                projects.map(proj => saveProjectEntry(proj))
            );
            // Parse dates from response
            const parsedEntries = savedEntries.map(proj => {
                const startParsed = parseDateString(proj.start_date);
                const endParsed = parseDateString(proj.end_date);
                return {
                    ...proj,
                    start_month: startParsed.month,
                    start_year: startParsed.year,
                    end_month: endParsed.month,
                    end_year: endParsed.year,
                };
            });
            setProjects(parsedEntries);
            onUpdate?.(savedEntries);
        } catch (error) {
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to save projects';
            setError?.(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const hasFieldError = (projIndex, field) => {
        if (!showErrors) return false;
        const proj = projects[projIndex];
        if (!proj) return false;
        
        if (field === 'name' && !proj.name?.trim()) return true;
        return false;
    };

    // Dropdown styles
    const selectStyles = {
        borderRadius: '6px',
        fontSize: '0.875rem',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d1d5db',
        },
    };

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>
                    Projects
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={addProject}
                        startIcon={<AddIcon />}
                        sx={{
                            color: '#4f46e5',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                        }}
                    >
                        Add Project
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveAll}
                        disabled={saving || projects.length === 0}
                        sx={{
                            bgcolor: '#4f46e5',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: '6px',
                            px: 3,
                            '&:hover': { bgcolor: '#4338ca' },
                        }}
                    >
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save All'}
                    </Button>
                </Box>
            </Box>

            {projects.length === 0 ? (
                <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    No projects added. Click "Add Project" to add one.
                </Typography>
            ) : (
                projects.map((proj, index) => (
                    <Box key={proj.id} sx={{ 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '8px', 
                        p: 3, 
                        mb: 2,
                        position: 'relative',
                    }}>
                        <IconButton
                            size="small"
                            onClick={() => removeProject(proj.id)}
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                Project Name<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Project name"
                                value={proj.name || ''}
                                onChange={(e) => updateProject(proj.id, 'name', e.target.value)}
                                sx={hasFieldError(index, 'name') ? getInputStyles(true, true) : inputStyles}
                            />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    Start Date
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 1 }}>
                                    <Select
                                        size="small"
                                        value={proj.start_month || ''}
                                        onChange={(e) => updateProject(proj.id, 'start_month', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={selectStyles}
                                    >
                                        <MenuItem value="">Month</MenuItem>
                                        {MONTHS.map(m => (
                                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                        ))}
                                    </Select>
                                    <Select
                                        size="small"
                                        value={proj.start_year || ''}
                                        onChange={(e) => updateProject(proj.id, 'start_year', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={selectStyles}
                                    >
                                        <MenuItem value="">Year</MenuItem>
                                        {YEAR_RANGE.map(y => (
                                            <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    End Date
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 1 }}>
                                    <Select
                                        size="small"
                                        value={proj.end_month || ''}
                                        onChange={(e) => updateProject(proj.id, 'end_month', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={selectStyles}
                                    >
                                        <MenuItem value="">Month</MenuItem>
                                        {MONTHS.map(m => (
                                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                        ))}
                                    </Select>
                                    <Select
                                        size="small"
                                        value={proj.end_year || ''}
                                        onChange={(e) => updateProject(proj.id, 'end_year', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={selectStyles}
                                    >
                                        <MenuItem value="">Year</MenuItem>
                                        {YEAR_RANGE.map(y => (
                                            <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                Description
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                multiline
                                rows={3}
                                placeholder="Describe the project, technologies used, and your contributions..."
                                value={proj.description || ''}
                                onChange={(e) => updateProject(proj.id, 'description', e.target.value)}
                                sx={inputStyles}
                            />
                        </Box>
                    </Box>
                ))
            )}
        </Box>
    );
});

ProjectsSection.displayName = 'ProjectsSection';

export default ProjectsSection;
